import { cloneElement, type ReactElement, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";

export interface TooltipProps {
  /** Plain text for almost every tooltip; a short block of structured content (e.g. two stacked lines) is fine too. */
  content: ReactNode;

  placement?: "top" | "right" | "bottom" | "left";

  // Typed so cloneElement can add aria-describedby under React 19's stricter
  // element typing (the generic ReactElement doesn't admit the prop).
  children: ReactElement<{ "aria-describedby"?: string }>;
  disabled?: boolean;
}

export function Tooltip({ content, placement = "top", children, disabled = false }: Readonly<TooltipProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen: isOpen && !disabled,
    anchorRef,
    placement,
    align: "center",
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const describedBy = [children.props["aria-describedby"], isOpen && !disabled ? tooltipId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={(event) => event.target.matches(":focus-visible") && setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        {cloneElement(children, { "aria-describedby": describedBy || undefined })}
      </span>
      {isOpen &&
        !disabled &&
        createPortal(
          <div
            ref={panelRef}
            id={tooltipId}
            role="tooltip"
            style={style}
            className="z-[110] inline-flex items-center whitespace-nowrap rounded-md bg-content-primary px-2 py-1 text-xs font-medium leading-none text-content-inverse"
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
